import { useRef, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import type { ProcurementQueueItem } from '../../shared/api/types'
import { uploadResponseAction } from './queueActions'

/**
 * Загрузка Excel-ответа поставщика на странице очереди: скрытый input, флаг
 * прогресса и текст-отчёт. Вынесено из ProcurementQueuePage (правило ≤500 строк).
 */
export function useQueueUpload(params: {
  items: ProcurementQueueItem[]
  queryClient: QueryClient
  invalidateQueue: () => void
}) {
  const { items, queryClient, invalidateQueue } = params
  const uploadRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<string | null>(null)

  const handleUploadResponse = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    setIsUploading(true)
    setUploadResult(null)
    try {
      const message = await uploadResponseAction({ file, items, queryClient, invalidateQueue })
      setUploadResult(message)
      setTimeout(() => setUploadResult(null), 6000)
    } catch (err) {
      // Сначала достаём серверный русский текст (err.response.data.error.message),
      // потом fallback на err.message. Голый axios «Request failed with status N»
      // в toast пользователю непонятен — см. инцидент 13.05 с 404 на /bulk
      // при устаревших requestItemId в Excel.
      const ex = err as {
        response?: { data?: { error?: { message?: string } } }
        message?: string
      }
      const msg =
        ex?.response?.data?.error?.message || (err instanceof Error ? err.message : String(err))
      setUploadResult('Ошибка: ' + msg)
      setTimeout(() => setUploadResult(null), 8000)
    } finally {
      setIsUploading(false)
    }
  }

  return { uploadRef, isUploading, uploadResult, handleUploadResponse }
}
